import { Outlet, useLocation } from 'react-router-dom';
import Header from '@components/ui/Header';
import Footer from '@components/ui/Footer';
import { Helmet } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import ScrollToTop from '../ui/ScrollToTop';
import Breadcrumbs from '../ui/Breadcrumbs';
import BackToTopButton from '../ui/BackToTopButton';
import SkipToContentLink from '../ui/SkipToContentLink';
import CommandPalette from '@components/ui/CommandPalette';
import FirebaseAuthSync from '@components/ui/SupabaseAuthSync';
import { MotionConfig } from 'framer-motion';

const MainLayout = () => {
  const location = useLocation();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const canonical = `${origin}${location.pathname}${location.search}`;

  // Breadcrumb JSON-LD
  const segments = location.pathname.split('/').filter(Boolean);
  const itemListElement = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
    ...segments.map((seg, idx) => ({
      '@type': 'ListItem',
      position: idx + 2,
      name: seg.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
      item: `${origin}/${segments.slice(0, idx + 1).join('/')}`,
    })),
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <FirebaseAuthSync />
      <MotionConfig reducedMotion="user">
        {/* Accessibility: Skip to content link */}
        <SkipToContentLink targetId="main-content" />

        {/* Scroll to top on route change */}
        <ScrollToTop />

        {/* Site-wide SEO defaults */}
        <Helmet>
          <link rel="canonical" href={canonical} />
          <meta name="theme-color" content="#1e3a8a" />
          <meta property="og:site_name" content="SORAN Kids Foundation" />
          <meta property="og:type" content="website" />
          <meta property="og:image" content="/assets/logos/soranlogo.jpg" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:image" content="/assets/logos/soranlogo.jpg" />
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: 'SORAN Kids Foundation',
              url: origin || 'https://example.com',
              logo: `${origin || ''}/assets/logos/soranlogo.jpg`,
              sameAs: [
                'https://www.linkedin.com/company/soran-kids-foundation',
              ],
            })}
          </script>
          {segments.length > 0 && (
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement,
              })}
            </script>
          )}
        </Helmet>

        <Header />

        {/* Breadcrumbs (hide on home) */}
        {location.pathname !== '/' && (
          <div className="bg-secondary-light/40">
            <div className="container py-2">
              <Breadcrumbs />
            </div>
          </div>
        )}

        <main id="main-content" className="flex-grow min-h-[50vh]">
          <Outlet />
        </main>

        <Footer />
        <BackToTopButton />
        <CommandPalette />
        <Analytics />
      </MotionConfig>
    </div>
  );
};

export default MainLayout;